/**
 * PwaProvider – Zentraler Provider für PWA-Aspekte:
 *   - Registriert Service Worker
 *   - Erkennt neue SW-Version (Update verfügbar)
 *   - Erkennt Offline-Zustand (navigator.onLine)
 *   - Speichert beforeinstallprompt Event (Chromium)
 *   - Erkennt iOS (kein beforeinstallprompt) für Anleitung „Zum Home-Bildschirm“
 */
import React, {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from "react";

const PwaContext = createContext(null);

const isIos = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const iosDevice = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = ua.includes("Mac") && "ontouchend" in document;
  return iosDevice || iPadOS;
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
};

export const PwaProvider = ({ children }) => {
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const [installEvent, setInstallEvent] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [updateReady, setUpdateReady] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);
  const waitingRef = useRef(null);
  const registrationRef = useRef(null);

  // ---- online/offline ----
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    try {
      window.addEventListener("online", on);
      window.addEventListener("offline", off);
    } catch { /* keine window-API verfügbar */ }
    return () => {
      try {
        window.removeEventListener("online", on);
        window.removeEventListener("offline", off);
      } catch { /* ignore */ }
    };
  }, []);

  // ---- beforeinstallprompt (nur Chromium; auf iOS ignoriert) ----
  useEffect(() => {
    // iOS Safari kennt das Event nicht – Listener trotzdem harmlos,
    // aber wir registrieren defensiv.
    if (isIos()) return;
    const handler = (e) => {
      try { e.preventDefault?.(); } catch { /* ignore */ }
      setInstallEvent(e);
    };
    const installedHandler = () => {
      setInstalled(true);
      setInstallEvent(null);
    };
    try {
      window.addEventListener("beforeinstallprompt", handler);
      window.addEventListener("appinstalled", installedHandler);
    } catch { /* ignore */ }
    return () => {
      try {
        window.removeEventListener("beforeinstallprompt", handler);
        window.removeEventListener("appinstalled", installedHandler);
      } catch { /* ignore */ }
    };
  }, []);

  // ---- Service Worker registrieren + Updates erkennen ----
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    // Auf iOS in privater Browsing-Session kann register() werfen –
    // wir schlucken alle Fehler defensiv.
    let cancelled = false;
    let intervalId = null;
    const register = async () => {
      try {
        const reg = await navigator.serviceWorker.register("/sw.js");
        if (cancelled) return;
        registrationRef.current = reg;

        const checkWaiting = () => {
          try {
            if (reg.waiting && navigator.serviceWorker.controller) {
              waitingRef.current = reg.waiting;
              setUpdateReady(true);
            }
          } catch { /* ignore */ }
        };
        checkWaiting();

        try {
          reg.addEventListener("updatefound", () => {
            const newWorker = reg.installing;
            if (!newWorker) return;
            newWorker.addEventListener("statechange", () => {
              try {
                if (
                  newWorker.state === "installed" &&
                  navigator.serviceWorker.controller
                ) {
                  waitingRef.current = reg.waiting || newWorker;
                  setUpdateReady(true);
                }
              } catch { /* ignore */ }
            });
          });
        } catch { /* ignore */ }

        // Alle 30 Minuten defensiv auf Updates prüfen
        intervalId = setInterval(() => {
          try { reg.update().catch(() => {}); } catch { /* ignore */ }
        }, 30 * 60_000);
      } catch { /* SW-Registrierung fehlgeschlagen (z.B. iOS private) */ }
    };
    register();

    // Beim Controller-Wechsel (neue SW aktiv) automatisch neu laden
    let reloaded = false;
    const onControllerChange = () => {
      if (reloaded) return;
      reloaded = true;
      try { window.location.reload(); } catch { /* ignore */ }
    };
    try {
      navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
    } catch { /* ignore */ }
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      try {
        navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      } catch { /* ignore */ }
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (installEvent) {
      try {
        installEvent.prompt();
        const choice = await installEvent.userChoice;
        setInstallEvent(null);
        return choice?.outcome === "accepted";
      } catch (_e) { /* PWA-Install abgebrochen */ return false; }
    }
    if (isIos() && !installed) {
      setShowIosHint(true);
      return false;
    }
    return false;
  }, [installEvent, installed]);

  const applyUpdate = useCallback(() => {
    try {
      const worker = waitingRef.current || registrationRef.current?.waiting;
      if (worker) worker.postMessage({ type: "SKIP_WAITING" });
    } catch { /* ignore */ }
  }, []);

  const canInstall = !installed && (installEvent !== null || isIos());
  const platform = isIos() ? "ios" : "chromium";

  const value = {
    online, updateReady, installed, canInstall, platform,
    promptInstall, applyUpdate,
    showIosHint, setShowIosHint,
  };

  return <PwaContext.Provider value={value}>{children}</PwaContext.Provider>;
};

export const usePwa = () => {
  const ctx = useContext(PwaContext);
  if (!ctx) {
    // Fallback für Tests
    return {
      online: true, updateReady: false, installed: false, canInstall: false,
      platform: "chromium", promptInstall: () => {}, applyUpdate: () => {},
      showIosHint: false, setShowIosHint: () => {},
    };
  }
  return ctx;
};
